import type { FacilityEquipment } from '@reliance/types/models/FacilityEquipment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import AddEditModal from '../AddEditModal';
import { useFacilityEquipmentList } from '../../hooks/FacilityEquipment';
import { useNotifyTabLoading } from '../../hooks/useTabLoadingReporter';
import { useAlerts } from '../../alert/AlertContext';

type ListProps = {
  facilityId?: string | number;
  onFetch?: () => void;
  onLoad?: () => void;
  onError?: () => void;
};

type FilterState = {
  name: string;
  serialNumber: string;
  description: string;
  type: string;
};

function searchInsensitive(haystack: string, needle: string): boolean {
  const n = needle.trim().toLowerCase();
  if (n === '') {
    return true;
  }
  return haystack.toLowerCase().includes(n);
}

const List: React.FC<ListProps> = ({ facilityId, onFetch, onLoad, onError }) => {
  const alerts = useAlerts();
  const facilityIdNum = useMemo(
    () => (facilityId != null ? Number(facilityId) : undefined),
    [facilityId]
  );

  const { execute: refetch, result: rows, loading, error } = useFacilityEquipmentList(facilityIdNum);

  useNotifyTabLoading(facilityIdNum != null ? 'equipment' : null, Boolean(facilityIdNum != null && loading));

  const [filters, setFilters] = useState<FilterState>({
    name: '',
    serialNumber: '',
    description: '',
    type: '',
  });

  const [modalShow, setModalShow] = useState(false);
  const [editEquipment, setEditEquipment] = useState<FacilityEquipment | null>(null);

  useEffect(() => {
    if (loading) {
      onFetch?.();
    }
  }, [loading, onFetch]);

  useEffect(() => {
    if (!loading && rows !== undefined) {
      onLoad?.();
    }
  }, [loading, rows, onLoad]);

  useEffect(() => {
    if (error != null) {
      alerts.error('An error occurred while loading equipment');
      onError?.();
    }
  }, [error, onError]);

  const filtered = useMemo(() => {
    const list = rows ?? [];
    return list.filter((r) => {
      return (
        searchInsensitive(r.name ?? '', filters.name) &&
        searchInsensitive(r.serialNumber ?? '', filters.serialNumber) &&
        searchInsensitive(r.description ?? '', filters.description) &&
        searchInsensitive(r.type ?? '', filters.type)
      );
    });
  }, [rows, filters]);

  const setFilter = useCallback((key: keyof FilterState, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const openAdd = useCallback(() => {
    setEditEquipment(null);
    setModalShow(true);
  }, []);

  const openEdit = useCallback((equipment: FacilityEquipment) => {
    setEditEquipment(equipment);
    setModalShow(true);
  }, []);

  const closeModal = useCallback(
    (saved: boolean) => {
      setModalShow(false);
      setEditEquipment(null);
      if (saved && facilityIdNum != null) {
        void refetch(facilityIdNum);
      }
    },
    [facilityIdNum, refetch]
  );

  const facilityForModal =
    facilityIdNum != null ? { id: facilityIdNum } : null;

  return (
    <>
      <Button
        type="button"
        variant="primary"
        className="btn-labeled mb-2"
        disabled={facilityIdNum == null}
        onClick={openAdd}>
        <span className="btn-label">
          <i className="fas fa-plus-circle" />{' '}
        </span>
        Add Equipment
      </Button>

      <table className="table table-striped table-hover table-bordered mt-3">
        <thead>
          <tr>
            <th>Name</th>
            <th>Serial Number</th>
            <th>Description</th>
            <th>Type</th>
          </tr>
          <tr>
            <th>
              <Form.Control
                size="sm"
                value={filters.name}
                placeholder="Filter"
                onChange={(e) => setFilter('name', e.target.value)}
              />
            </th>
            <th>
              <Form.Control
                size="sm"
                value={filters.serialNumber}
                placeholder="Filter"
                onChange={(e) => setFilter('serialNumber', e.target.value)}
              />
            </th>
            <th>
              <Form.Control
                size="sm"
                value={filters.description}
                placeholder="Filter"
                onChange={(e) => setFilter('description', e.target.value)}
              />
            </th>
            <th>
              <Form.Control
                size="sm"
                value={filters.type}
                placeholder="Filter"
                onChange={(e) => setFilter('type', e.target.value)}
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {loading && filtered.length === 0 && (
            <tr>
              <td colSpan={4} className="text-muted">
                Loading…
              </td>
            </tr>
          )}
          {!loading && filtered.length === 0 && (
            <tr>
              <td colSpan={4} className="text-muted">
                No equipment.
              </td>
            </tr>
          )}
          {filtered.map((equipment) => (
            <tr
              key={equipment.id}
              style={{ cursor: 'pointer' }}
              onClick={() => openEdit(equipment)}>
              <td>{equipment.name}</td>
              <td>{equipment.serialNumber}</td>
              <td>{equipment.description}</td>
              <td>{equipment.type}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <AddEditModal
        facility={facilityForModal}
        show={modalShow}
        editEquipment={editEquipment}
        onClose={closeModal}
      />
    </>
  );
};

export default List;
